import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import isEmpty from 'lodash/isEmpty'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { z } from 'zod'

import { Button } from '@/components/ui/button'
import { CalendarDatePicker } from '@/components/ui/calendar/calendar-date-picker'
import { Dialog } from '@/components/ui/dialog'
import PreferencesIcon from '@/modules/chatting/icons/preferences.svg'
import type { GetUsersListFilters } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { SelectWithControls } from './SelectWithControls'
import { UsersListFiltersField } from './UsersListFiltersField'
import {
  USERS_FILTER_FORM_ID,
  defaultValues,
  fieldsWithControls,
  schema,
} from './consts'

export type NewMessageUserFiltersFormValues = z.infer<typeof schema>

interface Props {
  query?: string
  filters: GetUsersListFilters
  setFilters: Dispatch<SetStateAction<GetUsersListFilters>>
  includeSignedUpFilters?: boolean
}

export const UsersListFilters = ({
  query,
  filters,
  setFilters,
  includeSignedUpFilters = true,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const form = useForm<NewMessageUserFiltersFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  useEffect(() => {
    if (isEmpty(filters)) {
      form.reset(defaultValues)
    }
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          form.setValue(key as keyof GetUsersListFilters, {
            isActive: true,
            value,
          })
        }
      })
    }
  }, [filters, form])

  const handleClose = () => {
    setIsModalOpen(false)
  }

  const onSubmit = (values: NewMessageUserFiltersFormValues) => {
    const filters = Object.entries(values).reduce<GetUsersListFilters>(
      (filters, [key, { value, isActive }]) => {
        if (isActive && !!value) {
          filters[key as keyof GetUsersListFilters] = value as any
        }

        return filters
      },
      {},
    )

    setFilters(filters)
    handleClose()
  }

  return (
    <>
      <IconButton
        icon={PreferencesIcon}
        onClick={() => setIsModalOpen(true)}
        className={classNames({ 'text-primary-500': !isEmpty(filters) })}
        disabled={!!query}
        tooltip="Filter Fans"
      />

      <Dialog
        bigDialog
        show={isModalOpen}
        panelClassName="lg:max-w-[500px]"
        onClose={handleClose}
      >
        <div className="space-y-6">
          <Dialog.Header>
            <Dialog.Title>Filter</Dialog.Title>
          </Dialog.Header>
          <FormProvider {...form}>
            <form
              id={USERS_FILTER_FORM_ID}
              className="space-y-3 rounded-lg border border-border-secondary p-4"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {fieldsWithControls.map(({ name, title, options }) => (
                <UsersListFiltersField
                  key={name}
                  name={name}
                  label={
                    <div className="flex flex-1 items-center justify-between gap-2">
                      <p className="flex-1 text-sm font-medium">{title}</p>
                      <Controller
                        name={`${name}.value`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <SelectWithControls
                            options={options}
                            value={String(value)}
                            onChange={(value) => {
                              form.setValue(`${name}.isActive`, true)
                              onChange(Number(value))
                            }}
                          />
                        )}
                      />
                    </div>
                  }
                />
              ))}
              <UsersListFiltersField
                name="recommenders"
                label={
                  <p className="flex-1 text-sm font-medium">
                    Show only recommendations
                  </p>
                }
              />
              <UsersListFiltersField
                name="online"
                label={<p className="flex-1 text-sm font-medium">Online</p>}
              />
              {includeSignedUpFilters && (
                <>
                  <div className="border-border-secondary w-full border-b" />
                  <UsersListFiltersField
                    className="items-start"
                    name="signed_up_after"
                    label={
                      <div className="flex-1 space-y-1.5">
                        <p className="flex-1 text-sm font-medium">
                          Who signed up after
                        </p>
                        <Controller
                          control={form.control}
                          name="signed_up_after.value"
                          render={({ field: { value, onChange } }) => (
                            <CalendarDatePicker
                              className="w-full"
                              date={value ? new Date(value) : undefined}
                              onChange={(date) => {
                                form.setValue('signed_up_after.isActive', true)
                                onChange(date?.toISOString())
                              }}
                            />
                          )}
                        />
                      </div>
                    }
                  />
                  <UsersListFiltersField
                    className="items-start"
                    name="signed_up_before"
                    label={
                      <div className="flex-1 space-y-1.5">
                        <p className="flex-1 text-sm font-medium">
                          Who signed up before
                        </p>
                        <Controller
                          control={form.control}
                          name="signed_up_before.value"
                          render={({ field: { value, onChange } }) => (
                            <CalendarDatePicker
                              className="w-full"
                              date={value ? new Date(value) : undefined}
                              onChange={(date) => {
                                form.setValue('signed_up_before.isActive', true)
                                onChange(date?.toISOString())
                              }}
                            />
                          )}
                        />
                      </div>
                    }
                  />
                </>
              )}
            </form>
          </FormProvider>
          <Dialog.ClosableFooter
            onClose={handleClose}
            className="grid-cols-3"
          >
            <Button
              variant="outline"
              onClick={() => form.reset(defaultValues)}
            >
              Reset
            </Button>
            <Button
              form={USERS_FILTER_FORM_ID}
              type="submit"
            >
              Apply
            </Button>
          </Dialog.ClosableFooter>
        </div>
      </Dialog>
    </>
  )
}
